import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import join from '@nullvoxpopuli/ember-composable-helpers/helpers/join';
import { isTesting } from '@embroider/macros';
import gt from 'ember-truth-helpers/helpers/gt';
import Progress from './progress.gts';
import { STYLES } from './validated-field.gts';

type FileObject = {
  mimeType?: string;
  index: number;
  fileContentAttributes: { data?: string };
};

interface Signature {
  Args: {
    onSelected: (file: FileObject) => void;
    onDestroy: (index: number) => void;
    acceptedTypes: string[];
  };
}

export default class FileUpload extends Component<Signature> {
  <template>
    <div class='labeled icon primary' data-test-upload-file-buttons>
      <div class='ui buttons'>
        <label for={{this.htmlInputId}} class='ui icon button'>
          <i class='file icon'></i><span class='ml4'>Joindre un document</span>
        </label>
      </div>
      <input
        type='file'
        id={{this.htmlInputId}}
        class='hidden'
        accept={{join ', ' @acceptedTypes}}
        {{on 'change' this.upload}}
      />
      <span class='ui small text grey ml4'>Moins de 10&nbsp;Mo. (Formats
        autorisés : PDF, PNG, JPEG, JPG, RTF)</span>
    </div>

    {{#if (gt this.progress 0)}}
      <Progress @progress={{this.progress}}>
        {{#if this.fileIsUploaded}}
          Ajouté avec succès
        {{else}}
          En cours :
          {{this.progress}}%
        {{/if}}
      </Progress>
    {{/if}}

    {{#each this.fileNames as |fileName index|}}
      <p class='mt-2' data-test-file-name>
        {{fileName}}
        <i class='green check icon' data-test-valid-upload></i>
        <i
          role='button'
          class='trash alternate link icon'
          data-test-delete-btn
          {{on 'click' (fn this.destroyFile index)}}
        ></i>
      </p>
    {{/each}}

    {{#if this.maxFileSizeReached}}
      <div class={{this.errorStyles}} data-test-file-size-exceed>La taille du
        fichier est supérieure à 10&nbsp;Mo.</div>
    {{/if}}

    {{#if this.fileMimeTypeUnauthorized}}
      <div class={{this.errorStyles}} data-test-file-unauthorized-type>
        Le format du fichier n'est pas autorisé.
      </div>
    {{/if}}
  </template>

  errorStyles = STYLES['left-aligned-validation-error-message'];

  declare htmlInputId: string;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    this.htmlInputId = guidFor(this);
  }

  /**
   * The maximum size of the file, in bytes.
   * Currently, it's 1Mb in test, 10 Mb otherwise
   */
  maxFileSize = isTesting() ? 1024 : 10485760;

  /**
   * A flag to show error message about file size.
   */
  @tracked
  maxFileSizeReached = false;

  /**
   * A flag to show error message about file mime type.
   */
  @tracked
  fileMimeTypeUnauthorized = false;

  /**
   * A flag indicating if a file is correctly uploaded (client-side).
   */
  @tracked
  fileIsUploaded = false;

  /**
   * An array containing the names of each file selected to be uploaded
   */
  @tracked
  fileNames: string[] = [];

  /**
   * The progress uploading the file in the browser
   */
  @tracked progress = 0;

  /**
   * The upload action is called when the file input changes.
   */
  upload = (event: Event) => {
    this.fileIsUploaded = false;
    const elementFiles = (event.target as HTMLInputElement).files;
    const file = elementFiles ? elementFiles[0] : null;

    if (!file) {
      /**
       * if the user clicked on "cancel", nothing changes
       */
      return;
    }

    this.maxFileSizeReached = false;
    this.fileMimeTypeUnauthorized = false;
    const reader = new FileReader();

    if (file.size > this.maxFileSize) {
      this.maxFileSizeReached = true;
      reader.abort();
      return;
    }

    if (!this.args.acceptedTypes.includes(file.type)) {
      this.fileMimeTypeUnauthorized = true;
      reader.abort();
      return;
    }

    reader.readAsDataURL(file);

    reader.onload = () => {
      const fileObject = {
        // @ts-expect-error this code needs a rewrite
        mimeType: reader.result?.toString().split(';')[0].split(':')[1],
        index: this.fileNames.length,
        fileContentAttributes: { data: reader.result?.toString().split(',')[1] }
      };
      this.args.onSelected(fileObject);
    };

    /**
     * While the file is uploading, we update the progress bar.
     * As it's currently applying to client-side uploading,
     * it's quite fast. Perhaps, it gives some feedback to the user,
     * and it may be used in the future for a server-side upload.
     */
    reader.addEventListener('progress', event => {
      if (event.loaded && event.total) {
        this.progress = (event.loaded / event.total) * 100;
      }
    });

    /**
     * Once the file successfully uploaded, we
     * push its name in the fileNames array, set
     * the flag fileIsUploaded to true and call
     * the transition 2 seconds later
     */
    reader.onloadend = () => {
      this.fileIsUploaded = true;
      this.fileNames = [...this.fileNames, file.name];
      setTimeout(() => {
        this.progress = 0;
      }, 2000);
    };
  };

  /**
   * When a file is destroyed,
   * we removed its name from the fileNames
   * array, reset some fields and we call
   * the function passed as an arg.
   */
  destroyFile = (index: number) => {
    this.args.onDestroy(index);
    this.fileNames.splice(index, 1);
    this.fileNames = [...this.fileNames];
    this.progress = 0;
    (document.getElementById(this.htmlInputId) as HTMLInputElement).value = '';
  };
}
